import {
  EnvironmentMonitoringItem,
  EnvironmentSensorType,
  getEnvironmentSensorDescription,
  getLabelColor,
} from '@mgh-app/component-library';
import { MainLayoutContext } from 'layouts/MainLayout';
import { ReactNode, useContext, useEffect } from 'react';
import { useParams } from 'react-router';
import { selectRoomById } from 'services/RoomService';
import { selectSensors } from 'services/ThingsService';
import { useAppSelector } from 'store';
import {
  alignItems,
  borderColor,
  borderWidth,
  combineCls,
  display,
  flexDirection,
  fontSize,
  fontWeight,
  gap,
  justifyContent,
  justifyItems,
  margin,
  overflow,
  padding,
  twCls,
  width,
} from 'style';

interface AirQualitySensorItemProps {
  title: ReactNode;
  type: EnvironmentSensorType;
  value: number | undefined;
  label: string | undefined;
}

const AirQualitySensorItem = ({
  title,
  type,
  value,
  label,
}: AirQualitySensorItemProps): JSX.Element => {
  return (
    <div className={twCls(display('flex'), flexDirection('flex-col'), width('w-full'))}>
      <div className={twCls(padding('pl-2'))}>{title}</div>
      <div className={twCls(display('flex'), padding('pt-3', 'pb-4'))}>
        <div className={twCls(padding('pt-1'))}>
          <EnvironmentMonitoringItem type={type} value={value} label={label} />
        </div>
        <div className={twCls(fontSize('text-xs'), display('flex'), alignItems('items-center'))}>
          <span>
            <span
              className={combineCls(getLabelColor(type, label), twCls(fontWeight('font-bold')))}
            >
              {label}
            </span>{' '}
            {getEnvironmentSensorDescription(type, label)}
          </span>
        </div>
      </div>
      <div className={twCls(borderWidth('border-t'), borderColor('border-grey-200'))}></div>
    </div>
  );
};

export const Sensors = (): JSX.Element => {
  const { setTitle, showNavigationButtons } = useContext(MainLayoutContext);
  const { roomId = '', sensorsId = '' } = useParams();
  const room = useAppSelector((state) => selectRoomById(state, roomId));
  const sensors = useAppSelector((state) => selectSensors(state, sensorsId));

  useEffect(() => {
    room?.nickName && setTitle(room?.nickName);
    showNavigationButtons(true);
  }, [room, room?.nickName, setTitle, showNavigationButtons]);

  const getSensorValue = (sensorType: EnvironmentSensorType) =>
    sensors?.find((s) => s.type === sensorType)?.value ?? undefined;

  const getSensorLabel = (sensorType: string) =>
    sensors?.find((s) => s.type === sensorType)?.label ?? undefined;

  return (
    <div
      className={twCls(
        margin('mt-6'),
        display('grid'),
        justifyContent('justify-center'),
        justifyItems('justify-items-center'),
      )}
    >
      <h2>Environmental Monitoring</h2>
      <div
        className={twCls(
          width('w-full'),
          margin('mt-6'),
          overflow('overflow-auto'),
          display('grid'),
          gap('gap-6'),
        )}
      >
        {sensors && (
          <>
            <AirQualitySensorItem
              title="Carbon Dioxide"
              type={EnvironmentSensorType.CO2}
              value={getSensorValue(EnvironmentSensorType.CO2)}
              label={getSensorLabel(EnvironmentSensorType.CO2)}
            />
            <AirQualitySensorItem
              title="Volatile Organic Compounds (VOC)"
              type={EnvironmentSensorType.VOC}
              value={getSensorValue(EnvironmentSensorType.VOC)}
              label={getSensorLabel(EnvironmentSensorType.VOC)}
            />
            <AirQualitySensorItem
              title="Humidity"
              type={EnvironmentSensorType.RH}
              value={getSensorValue(EnvironmentSensorType.RH)}
              label={getSensorLabel(EnvironmentSensorType.RH)}
            />
            <AirQualitySensorItem
              title="Light Level"
              type={EnvironmentSensorType.Lux}
              value={getSensorValue(EnvironmentSensorType.Lux)}
              label={getSensorLabel(EnvironmentSensorType.Lux)}
            />
            <AirQualitySensorItem
              title="Dew Point"
              type={EnvironmentSensorType.Dew}
              value={getSensorValue(EnvironmentSensorType.Dew)}
              label={getSensorLabel(EnvironmentSensorType.Dew)}
            />
            {getSensorValue(EnvironmentSensorType.Temperature) && (
              <AirQualitySensorItem
                title="Temperature"
                type={EnvironmentSensorType.Temperature}
                value={getSensorValue(EnvironmentSensorType.Temperature)}
                label={getSensorLabel(EnvironmentSensorType.Temperature)}
              />
            )}
            {getSensorValue(EnvironmentSensorType.AirPressure) && (
              <AirQualitySensorItem
                title="Air Pressure"
                type={EnvironmentSensorType.AirPressure}
                value={getSensorValue(EnvironmentSensorType.AirPressure)}
                label={getSensorLabel(EnvironmentSensorType.AirPressure)}
              />
            )}
            <AirQualitySensorItem
              title={
                <span>
                  PM<sub>2.5</sub>
                </span>
              }
              type={EnvironmentSensorType.PM25}
              value={getSensorValue(EnvironmentSensorType.PM25)}
              label={getSensorLabel(EnvironmentSensorType.PM25)}
            />
            <AirQualitySensorItem
              title={
                <span>
                  PM<sub>10</sub>
                </span>
              }
              type={EnvironmentSensorType.PM10}
              value={getSensorValue(EnvironmentSensorType.PM10)}
              label={getSensorLabel(EnvironmentSensorType.PM10)}
            />
          </>
        )}
      </div>
    </div>
  );
};
